import type { IconsId } from "@silversea-ssc/design-system-icons"
import type { CruiseGroupCode } from "@silversea-ssc/web-cruise-groups"
import type { FareCode } from "@silversea-ssc/web-fares-fp"
import { InclusionCode } from "@ssc-booking/api-contracts"
import { SUITE_FEATURE_GROUPINGS } from "@silversea-ssc/web-suite-features"
import { BlockContentValue } from "@silversea-ssc/ui-components-react"
import { Locale } from "../format/localization"
import { SanityQueryContent } from "./sanity-queries"

export type SanityLocale = "en" | "es" | "pt_br" | "de" | "fr"

export const toSanityLocale = (locale: Locale | undefined): SanityLocale => {
    switch (locale) {
        case "en":
            return "en"
        case "es":
            return "es"
        case "pt-br":
            return "pt_br"
        case "de":
            return "de"
        case "fr":
            return "fr"
        default:
            return "en"
    }
}

export type SanityImage = {
    _key?: string
    hotspot?: {
        height?: number
        width?: number
        x?: number
        y?: number
    }
    crop?: {
        bottom?: number
        left?: number
        right?: number
        top?: number
    }

    asset: {
        _id: string
        metadata?: {
            preview?: string
            dimensions?: {
                width?: number
                height?: number
            }
        }
    }

    caption?: string
    alt?: string
}

type SanityCard = {
    description: string
    image: SanityImage
    title: string
}

export type SanitySuite = {
    color: string
    card: SanityCard
    shipVersionKey: string
    configurations: {
        code: string
        shortSizeDescriptionInSquareFeet?: string
        shortSizeDescriptionInSquareMetres?: string
        verandaSizeInSquareFeet?: string
        verandaSizeInSquareMetres?: string
    }[]
    keyFeatures: {
        veranda: boolean
        livingRoom: boolean
    }
    deck: number[]
}

export type SanityDiningArea = {
    _key: string
    card: Pick<SanityCard, "title"> & { trackingName: string }
}

export type SanityFacility = {
    _key: string
    card: Pick<SanityCard, "title"> & { trackingName: string }
}

export type SanityShip = {
    _id: string
    card: SanityCard
    name: string
    version: {
        _key: string
        suites: SanitySuite[]
        lateralViewImage: SanityImage
        deckPlanDescription?: string
        passengerCount?: number
        crewCount?: number
        buildYear?: number
        lastRefurbishmentYear?: number
        tonnage?: number
        decks?: ShipDeckPlan[]
        deckPlanPdf?: {
            asset: {
                url: string
            }
        }
        diningAreas: SanityDiningArea[] | null | undefined
        facilities: SanityFacility[] | null | undefined
    }
}

export type ShipDeckPlan = {
    deckNumber: number
    deckImageURL: string
}

type SanityPort = {
    name: string
}

export type SanityDestination = {
    destinationWebCode: string
    name: string
    trackingName: string
    path: string
}

type SanitySpecialVoyageDestination = { title: string; trackingName: string; path: string }
type SanitySpecialVoyageDestinations = {
    grandVoyagesPage: SanitySpecialVoyageDestination
    worldCruisesPage: SanitySpecialVoyageDestination
}

type SanityCruise = {
    _type: "cruise" | "specialVoyage"
    cardImage: SanityImage
    cruiseCode: string
    path: string
}

export type SanityCruiseData = {
    _type: "cruiseData"
    destination: SanityDestination
    departurePort: SanityPort & { portImage: SanityImage; data: { code: string } }
    arrivalPort: SanityPort
    departureDate: string
    arrivalDate: string
    days: number
    cruiseType: string
    comboType: string
    ship: SanityShip
    cruiseGroup: CruiseGroupCode
}

export type SanitySpecialVoyageCruiseData = Omit<SanityCruiseData, "destination" | "_type"> & {
    _type: "specialVoyageData"
}

export type ContentAddress = {
    path: string
}

type WebsiteUrls = {
    termsAndConditions: ContentAddress
    findYourCruise: ContentAddress
}

export type SanityInclusionsContent = {
    _id: string
    codes: InclusionCode[]
    icon: IconsId
    infographicTitle: string
    title: BlockContentValue
    description: BlockContentValue
    footnote: string
    overrideContent: {
        cruiseGroupFilters: CruiseGroupCode | undefined | null
        infographicTitle: string
        title: BlockContentValue
        description: BlockContentValue
        footnote: string
    }[]
}

export type FareContent = {
    _id: string
    fareCodes: FareCode[]
    title: string
    headline?: string
}

export type CancellationPolicyModalSectionContent = {
    type?:
        | "by-guest-due-covid"
        | "by-guest-due-other-reasons"
        | "by-silversea-due-covid"
        | "by-silversea-due-other-reasons"
    title: string
    content: BlockContentValue
    disclaimer: string
    staticInfographicRefundableLabel: string
    staticInfographicFCCNote: string
}

export type SanityPenaltiesModalContent = {
    _id: string
    cancelledBy: CancellationPolicyModalSectionContent[]
}

export type SanityQueryResponseCruise = {
    cruiseData: SanityCruiseData | SanitySpecialVoyageCruiseData
    cruise?: SanityCruise
    specialCruiseDestination: SanitySpecialVoyageDestinations
    faresContent: FareContent[]
}

export type DynamicFAQVisibleInStep = "guests-and-fare" | "suite-category" | "guests-info" | "payment"
export type DynamicFAQContent = {
    _key: string
    title: string
    titleEN: string
    content: BlockContentValue | undefined
    visibleInSteps: DynamicFAQVisibleInStep[]
}

export type SanityQueryResponseMandatory = SanityQueryResponseCruise & {
    websiteUrls: WebsiteUrls
    cardPromoBadgesContent: CardPromoBadgesContent[]
    formDisclaimer: SanityFormDisclaimer
    termsAndConditionsModal: TermsAndConditionsModalContent | undefined
    dynamicFAQ: DynamicFAQContent[]
    inclusionsContent: SanityInclusionsContent[]
}

type TermsAndConditionsModalContent = {
    modalTitle: string
    modalContent: BlockContentValue
}

export type CardPromoBadgesContent = {
    code: string
    content?: {
        title?: string
        iconUrl?: string
        showInCruiseCard: boolean
    }
}

export type SanityQueryResponseOptional = {
    penaltiesModalContent: SanityPenaltiesModalContent
}
export type SanityQueryResponse = SanityQueryResponseMandatory & Partial<SanityQueryResponseOptional>

export type SanityShipSections = {
    _key?: string
    aft?: boolean
    forward?: boolean
    midShip?: boolean
    port?: boolean
    starboard?: boolean
}
export type SanityShipComparableFeatures = {
    _key: string
} & Record<
    (typeof SUITE_FEATURE_GROUPINGS)[0]["features"][0]["key"],
    (typeof SUITE_FEATURE_GROUPINGS)[0]["features"][0]["type"]
>

type SanitySuiteConfiguration = {
    code: string
    shortSizeDescriptionInSquareFeet: string
    shortSizeDescriptionInSquareMetres: string
    verandaSizeInSquareFeet: string
    verandaSizeInSquareMetres: string
    name: string
    plan: SanityImage
    configurationInformation: BlockContentValue
}

export type SanitySuiteDeck = {
    deckNumber: number
    deckImageURL: string
    deckSuiteCategories: {
        suiteCategory: { _ref: string }
    }[]
}

export type SanitySuiteModalQueryResponse = {
    version: {
        decks?: SanitySuiteDeck[]
        suites: {
            category: { _ref: string }
            card: Pick<SanityCard, "title">
            description: BlockContentValue
            images: SanityImage[]
            configurations: SanitySuiteConfiguration[]
            shipSections: SanityShipSections
            deck: number[]
            comparableFeatures: SanityShipComparableFeatures
            color: string
        }[]
    }
}

export type SanitySuiteComparison = {
    _key: string
    name: string
    image: SanityImage
    features: Record<string, string | boolean | SanityImage | undefined>
    deck: number[]
    shipSections: SanityShipSections
    configurations: SanitySuiteConfiguration[]
}

export type SanitySuitesComparisonModalQueryResponse = {
    version: {
        decks: { deckNumber: number }[]
        suites: SanitySuiteComparison[]
    }
}

export type SanityQueryResponseWithOptionalContent<K extends SanityQueryContent> = SanityQueryResponseMandatory &
    Pick<SanityQueryResponseOptional, K>

export type SanityPublicAreasQueryResponse = {
    version: {
        publicAreas: {
            _key: string
            card: Pick<SanityCard, "title"> & { trackingName: string }
            description: BlockContentValue
            images: SanityImage[]
        }[]
    }
}

export type SanityFormDisclaimer =
    | {
          disclaimer?: BlockContentValue
          overrideDisclaimer?: {
              content?: BlockContentValue
              locationFilters?: SanityLocationFilter[]
          }[]
      }
    | undefined

export type SanityLocationFilter = {
    _type: "locationFilter"
    locationFilter?: string
}
